import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import Header from './components/Header';
import CategoryMenu from './components/CategoryMenu';
import ProductGrid from './components/ProductGrid';
import OrderSummary from './components/OrderSummary';
import OrderConfirmation from './components/OrderConfirmation';
import LoginPage from './pages/LoginPage';
import AuthProvider from './context/AuthProvider';
import { useAuth } from './context/AuthContext';
import { CATEGORY_MAPPINGS } from './config/categoryColors';
import apiService from './services/api';
import './App.css';

// ─── POS UI ──────────────────────────────────────────────────────────────────
// Renders the main point-of-sale interface. Only shown when authenticated.
function AppContent() {
  const { isLoadingAuth, interceptorsReady } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);

  // Wait for axios interceptors before hitting the backend
  const ready = !isLoadingAuth && interceptorsReady;

  // Map category names to icons and colors using centralized configuration
  const categoryMappings = CATEGORY_MAPPINGS;

  // Load categories once interceptors are ready
  useEffect(() => {
    if (ready) loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Load products when category changes (only after interceptors are ready)
  useEffect(() => {
    if (ready && selectedCategory) {
      loadProductsByCategory(selectedCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, selectedCategory]);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const rawCategories = await apiService.getCategories();
      
      // Normalise: backend may return string[] or [{CategoryName:"..."},...] — always work with strings
      const categoryNames = (rawCategories || []).map(item =>
        typeof item === 'string' ? item : (item.CategoryName || item.categoryName || String(item))
      );
      
      // Map category name strings to objects with icons and colors
      const mappedCategories = categoryNames.map(name => ({
        id: name,
        name: name,
        icon: categoryMappings[name]?.icon || 'ShoppingBasket',
        color: categoryMappings[name]?.color || '#607d8b'
      }));
      
      setCategories(mappedCategories);
      
      // Set first category as default if none selected
      if (mappedCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(mappedCategories[0].id);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback: show a single placeholder so the UI doesn't break
      const fallbackCategories = [
        { id: 'General', name: 'General', icon: 'ShoppingBasket', color: '#607d8b' }
      ];
      setCategories(fallbackCategories);
      setSelectedCategory('General');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProductsByCategory = async (category) => {
    try {
      console.log('Loading products for category:', category);
      const categoryProducts = await apiService.getProductsByCategory(category);
      console.log('Received products:', categoryProducts.length, 'products');
      setProducts(categoryProducts);
    } catch (error) {
      console.error('Error loading products for category:', category, error);
      setProducts([]);
    }
  };

  const addToOrder = (product) => {
    const existingItem = orderItems.find(item => item.name === product.name);
    if (existingItem) {
      setOrderItems(orderItems.map(item => 
        item.name === product.name 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setOrderItems([...orderItems, {
        id: Date.now(),         // React list key
        productId: product.id,  // PyME ProductId for invoice
        name: product.name,
        price: product.price,
        quantity: 1,
        _raw: product._raw || null, // full PyME product record for invoice generation
      }]);
    }
  };

  const removeFromOrder = (id) => {
    setOrderItems(orderItems.filter(item => item.id !== id));
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity === 0) {
      removeFromOrder(id);
    } else {
      setOrderItems(orderItems.map(item => 
        item.id === id ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const clearOrder = () => {
    setOrderItems([]);
  };

  const getCurrentCategoryColor = () => {
    const currentCategory = categories.find(cat => cat.id === selectedCategory);
    return currentCategory ? currentCategory.color : null;
  };

  // Order action handlers
  const handleHoldOrder = () => {
    if (orderItems.length > 0) {
      setShowOrderConfirmation(true);
    }
  };

  const handleConfirmOrder = (paymentMethod, orderType = 'sale') => {
    // TODO: Save order to backend with payment method and order type
    console.log('Order confirmed with payment method:', paymentMethod);
    console.log('Order type:', orderType);
    console.log('Order data:', orderItems);
    
    // Clear order after confirmation
    clearOrder();
    setShowOrderConfirmation(false);
  };

  const handleModifyOrder = () => {
    // Just close the overlay to return to current state
    setShowOrderConfirmation(false);
  };

  const handleCancelOrder = () => {
    if (orderItems.length > 0) {
      clearOrder();
    }
  };

  return (
    <div className="app">
      <Header 
        orderItems={orderItems}
        onClearOrder={clearOrder}
        onProductSelect={addToOrder}
      />
      
      {isLoading ? (
        <div className="loading-container" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Cargando categorías y productos...
        </div>
      ) : (
        <div className="main-content">
          <div className="left-panel">
            <OrderSummary 
              orderItems={orderItems}
              onRemoveItem={removeFromOrder}
              onUpdateQuantity={updateQuantity}
            />
            <div className="order-actions">
              <button 
                className={`order-cancel-btn ${orderItems.length === 0 ? 'disabled' : ''}`}
                onClick={handleCancelOrder}
                disabled={orderItems.length === 0}
              >
                Cancelar
              </button>
              <button 
                className={`order-save-btn ${orderItems.length === 0 ? 'disabled' : ''}`}
                onClick={handleHoldOrder}
                disabled={orderItems.length === 0}
              >
                Confirmar
              </button>
            </div>
          </div>
          
          <div className="right-panel">
            <CategoryMenu 
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={setSelectedCategory}
            />
            <ProductGrid 
              products={products}
              onProductSelect={addToOrder}
              categoryColor={getCurrentCategoryColor()}
            />
          </div>
        </div>
      )}

      {/* Order Confirmation Modal */}
      {showOrderConfirmation && (
        <OrderConfirmation
          orderData={{
            items: orderItems,
            userName: 'Usuario A',
            orderNumber: `#${Date.now().toString().slice(-6)}`
          }}
          onConfirm={handleConfirmOrder}
          onModify={handleModifyOrder}
          onClose={() => setShowOrderConfirmation(false)}
        />
      )}
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
// Provides AuthProvider + BrowserRouter.
// Renders LoginPage when unauthenticated, AppContent when authenticated.
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoot />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoot() {
  const { isAuthenticated, isLoadingAuth, interceptorsReady } = useAuth();

  // Show a full-screen spinner while checking stored auth state
  if (isLoadingAuth || !interceptorsReady) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large">
          <div style={{ padding: 32, color: '#666' }}>Cargando...</div>
        </Spin>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/*"
        element={isAuthenticated ? <AppContent /> : <Navigate to="/login" replace />}
      />
    </Routes>
  );
}

export default App;
